import { useCallback, useState } from 'react';
import { Route } from 'react-router';
import { Routes } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import Badge from '@/design_system/Badge';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import InputSearch from '@/design_system/InputSearch';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { type Brand, useBrands, useDeleteBrand } from '@/models/brand';
import { DeleteButtonIconOnly } from '@/routes/SettingsBeta/components/DeleteButtonIconOnly';
import { useSettingsScope } from '@/routes/SettingsBeta/SettingsContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { Brand as BrandPage } from './Brand';

const BRANDS_PER_PAGE = 20;

export const BrandsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Brands />} />
      <Route path="/:id/*" element={<BrandPage />} />
    </Routes>
  );
};

const Brands = () => {
  const { scope } = useSettingsScope();
  const organizationId = scope?.scope === 'organization' ? scope.organizationId : undefined;

  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), []);
  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const {
    data: { brands, meta } = { brands: [] },
    isLoading: isBrandsLoading,
    isPlaceholderData: isBrandsPlaceholder,
    isError: isBrandsError,
  } = useBrands(
    {
      organizationId,
      limit: BRANDS_PER_PAGE,
      offset: (page - 1) * BRANDS_PER_PAGE,
      search: debouncedSearch || undefined,
    },
    {
      keepPreviousData: true,
      refetchOnMount: true,
    }
  );

  const columnsWidths = {
    name: '1fr',
    contractualObligation: '1fr',
    depositPercentages: '1fr',
    deleteButton: 'auto',
  };

  return (
    <main style={{ gap: '1rem' }}>
      <BrandsSearch search={search} debouncedSetSearch={debouncedSetSearch} />

      <TableQueryWrapper isLoading={isBrandsLoading || isBrandsPlaceholder} isError={isBrandsError}>
        {brands && brands.length === 0 ? (
          <BrandsEmptyState />
        ) : (
          <Table columnWidths={Object.values(columnsWidths)} bordered>
            <BrandsHeader />

            <BrandsBody brands={brands} />

            <PaginationFooter
              itemsPerPage={BRANDS_PER_PAGE}
              page={page}
              onPageChange={setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const BrandsSearch = ({
  search,
  debouncedSetSearch,
}: {
  search: string;
  debouncedSetSearch: (search: string) => void;
}) => {
  const { t } = useLingui();

  return (
    <div>
      <InputSearch
        ariaLabel={t({
          id: 'settings.organization.data-catalogs.brands.search.label',
          message: 'Search brands',
        })}
        placeholder={t({
          id: 'settings.organization.data-catalogs.brands.search.placeholder',
          message: 'Search brands...',
        })}
        value={search}
        onChange={debouncedSetSearch}
        style={{ flex: 1 }}
      />
    </div>
  );
};
const BrandsHeader = () => {
  const { t } = useLingui();

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="settings.organization.data-catalogs.brands.table.header.name">Name</Trans>
        </Column>

        <Column>
          <Trans id="settings.organization.data-catalogs.brands.table.header.contractual-obligation">
            Contractual obligation to handle
          </Trans>
        </Column>

        <Column>
          <Trans id="settings.organization.data-catalogs.brands.table.header.deposit-percentages">
            Deposit percentages
          </Trans>
        </Column>

        <Column
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.brands.table.header.delete',
            message: 'Delete brand',
          })}
        />
      </Row>
    </Header>
  );
};

const BrandsBody = ({ brands }: { brands: Brand[] }) => {
  return (
    <Body>
      {brands?.map((brand) => (
        <BrandRow key={brand.id} brand={brand} />
      ))}
    </Body>
  );
};

const BrandRow = ({ brand }: { brand: Brand }) => {
  const { t } = useLingui();
  const { mutateAsync: deleteBrand } = useDeleteBrand(brand.id);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const brandName = brand.name;

  return (
    <Row to={`/settings_beta/catalog/brands/${brand.id}`}>
      <Cell isLink>
        <Stack row gap="0.5rem">
          <Paragraph weight="medium">{brand.name}</Paragraph>
          {brand.ownedByOrganization && (
            <Badge color="tertiary">
              <Trans id="settings.organization.data-catalogs.brands.table.header.owned-by-organization">
                Owned by organization
              </Trans>
            </Badge>
          )}
        </Stack>
      </Cell>

      <Cell>
        <Paragraph>
          {brand.contractualObligationToHandle ? (
            <Trans id="settings.organization.data-catalogs.brands.table.header.contractual-obligation.yes">
              Yes
            </Trans>
          ) : (
            <Trans id="settings.organization.data-catalogs.brands.table.header.contractual-obligation.no">
              No
            </Trans>
          )}
        </Paragraph>
      </Cell>

      <Cell>
        <Paragraph>{brand.depositPercentages.join(', ')}</Paragraph>
      </Cell>

      <Cell justifyContent="flex-end">
        <DeleteButtonIconOnly
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.brands.table.header.delete-brand-name',
            message: `Delete brand ${brandName}`,
          })}
          onClick={() => setShowDeleteConfirmation(true)}
        />
        <ConfirmationDialog
          showDialog={showDeleteConfirmation}
          setShowDialog={setShowDeleteConfirmation}
          title={
            <Trans id="settings.organization.data-catalogs.brands.delete.confirmation.title">
              Delete brand
            </Trans>
          }
          confirm={
            <Trans id="settings.organization.data-catalogs.brands.delete.confirmation.confirm">
              Delete
            </Trans>
          }
          onConfirm={() => deleteBrand()}
        >
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.brands.delete.confirmation.description">
              Are you sure you want to delete the brand &quot;{brandName}&quot;? This action cannot
              be undone.
            </Trans>
          </Paragraph>
        </ConfirmationDialog>
      </Cell>
    </Row>
  );
};

const BrandsEmptyState = () => {
  return (
    <EmptyState
      subtitle={
        <Stack>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.brands.no-results.1">
              No results are matching your criteria.
            </Trans>
          </Paragraph>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.brands.no-results.2">
              Please try with different keywords.
            </Trans>
          </Paragraph>
        </Stack>
      }
      illustration={<IllustrationNoResults />}
    />
  );
};
